import React, { useEffect, useState } from 'react'
import {
  AccordionDetails,
  AccordionSummary, Box,
  Card,
  CardContent, CircularProgress,
  Divider,
  Grid,
  Link,
  Stack, TextField,
  Typography
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AddIcon from '@mui/icons-material/Add'
import { accordionSummaryStyles, cardStyles } from '../basic-info/person-basic-information-styles'
import TextSnippetIcon from '@mui/icons-material/TextSnippet'
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import ForumIcon from '@mui/icons-material/Forum'
import {
  DragAndDropStack,
  IconButtonWithTooltip,
  useGlobalMessageHandler,
  membershipTypeOptions,
  MembershipTypeSelect,
  occupationTypeOptions,
  OccupationTypeSelect,
  publicationTypeOptions,
  PublicationTypeSelect,
  awardTypeOptions,
  AwardTypeSelect,
  BackgroundInfoCard,
  EnhancedAccordion,
  MaterialUIPickers,
  BioSourceSelect
  , BioTypeSelect
} from 'components'
import {
  type Award,
  type BaseBackgroundInfo,
  type Director,
  type Education,
  getPersonBackgroundInfo,
  type KnowWhoBio,
  type PersonBackgroundInfoResponse,
  type PersonBackgroundDataType,
  type HobbyInterest,
  type MilitaryService,
  type NotableFact,
  type OrgMembership,
  type PoliticalExperience,
  type PreviousOccupation,
  type PublicationCredit,
  type NarrativeBio,
  createBackgroundInfo,
  deleteBackgroundInfo,
  updateBackgroundInfo,
  updateBackgroundInfoDisplayOrder
} from 'services'
import {
  getEmptyKnowWhoBio, getEmptyNarrativeBio, getEmptyAward, getEmptyDirector, getEmptyEducation,
  getEmptyHobbyInterest, getEmptyMilitaryService, getEmptyNotableFact, getEmptyOrgMembership,
  getEmptyPoliticalExperience, getEmptyPreviousOccupation, getEmptyPublicationCredit
} from 'helpers'
import { formatErrorFieldMessages, type HttpResponse } from 'interfaces'
import dayjs from 'dayjs'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined'
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter'
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined'
import SchoolIcon from '@mui/icons-material/School'
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined'
import SportsEsportsIcon from '@mui/icons-material/SportsEsports'
import SportsEsportsOutlinedIcon from '@mui/icons-material/SportsEsportsOutlined'
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech'
import MilitaryTechOutlinedIcon from '@mui/icons-material/MilitaryTechOutlined'
import StarIcon from '@mui/icons-material/Star'
import StarOutlineIcon from '@mui/icons-material/StarOutline'
import GroupIcon from '@mui/icons-material/Group'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import HowToVoteIcon from '@mui/icons-material/HowToVote'
import HowToVoteOutlinedIcon from '@mui/icons-material/HowToVoteOutlined'
import WorkIcon from '@mui/icons-material/Work'
import WorkOutlineIcon from '@mui/icons-material/WorkOutline'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined'
import ReorderIcon from '@mui/icons-material/Reorder'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import {
  type DirectorBoardType,
  directorBoardTypeOptions,
  DirectorBoardTypeSelect
  , type Degree, degreeOptions, DegreeSelect
} from 'components'

interface PersonBasicInfoProps {
  personId: number
  startExpanded?: boolean
}

const hasValue = (value: string | null | undefined): value is string => value != null && value !== ''

const formatRange = (start: string | null | undefined, end: string | null | undefined): string => {
  if (hasValue(start) && hasValue(end)) return `(${start} - ${end})`
  if (hasValue(start)) return `(${start} - )`
  if (hasValue(end)) return `(${end})`
  return ''
}

const DataType: Record<string, PersonBackgroundDataType> = {
  KW_BIO: {
    code: 'kw-bio',
    description: 'KnowWho Biography',
    key: 'background_infos',
    icon: TextSnippetIcon,
    selectedIcon: TextSnippetOutlinedIcon
  },
  NARRATIVE_BIO: {
    code: 'narrative-bio',
    description: 'Narrative Biography',
    key: 'narrative_bios',
    icon: ForumIcon,
    selectedIcon: ForumOutlinedIcon
  },
  AWARDS: {
    code: 'awards',
    description: 'Awards',
    key: 'awards',
    icon: EmojiEventsIcon,
    selectedIcon: EmojiEventsOutlinedIcon
  },
  DIRECTORS: {
    code: 'board-directors',
    description: 'Board of Director',
    key: 'directors',
    icon: BusinessCenterIcon,
    selectedIcon: BusinessCenterOutlinedIcon
  },
  EDUCATION: {
    code: 'education',
    description: 'Education',
    key: 'education',
    icon: SchoolIcon,
    selectedIcon: SchoolOutlinedIcon
  },
  HOBBIES_INTERESTS: {
    code: 'hobbies-interests',
    description: 'Hobbies and Interests',
    key: 'interests',
    icon: SportsEsportsIcon,
    selectedIcon: SportsEsportsOutlinedIcon
  },
  MILITARY_SERVICE: {
    code: 'military-service',
    description: 'Military Service',
    key: 'military_service',
    icon: MilitaryTechIcon,
    selectedIcon: MilitaryTechOutlinedIcon
  },
  NOTABLE_FACTS: {
    code: 'notable-facts',
    description: 'Notable Facts',
    key: 'facts',
    icon: StarIcon,
    selectedIcon: StarOutlineIcon
  },
  ORG_MEMBERSHIPS: {
    code: 'org-memberships',
    description: 'Organization Memberships',
    key: 'memberships',
    icon: GroupIcon,
    selectedIcon: GroupOutlinedIcon
  },
  POLITICAL_EXPERIENCE: {
    code: 'political-experience',
    description: 'Political Experience',
    key: 'experience',
    icon: HowToVoteIcon,
    selectedIcon: HowToVoteOutlinedIcon
  },
  PREVIOUS_OCCUPATIONS: {
    code: 'previous-occupations',
    description: 'Previous Occupations',
    key: 'occupations',
    icon: WorkIcon,
    selectedIcon: WorkOutlineIcon
  },
  PUBLICATION_CREDITS: {
    code: 'publication-credits',
    description: 'Publication Credits',
    key: 'publications',
    icon: MenuBookIcon,
    selectedIcon: MenuBookOutlinedIcon
  }
}

export const PersonBackgroundInfoBlock: React.FC<PersonBasicInfoProps> = ({ personId, startExpanded = false }) => {
  const [expanded, setExpanded] = useState(startExpanded)
  const { showErrorMessage, showSuccessMessage } = useGlobalMessageHandler()
  const [loading, setLoading] = useState(true)
  const [backgroundInfo, setBackgroundInfo] = useState<PersonBackgroundInfoResponse | null>(null)
  const [selectedDataType, setSelectedDataType] = useState<PersonBackgroundDataType>(DataType.KW_BIO)
  const [title, setTitle] = useState(DataType.KW_BIO.description)
  const [numberOfRecords, setNumberOfRecords] = useState<number>(0)
  const [expandedStates, setExpandedStates] = useState<Record<number, boolean>>({})
  const [creatingItems, setCreatingItems] = useState<Record<string, boolean>>({})
  const [narrativeBioReorderingEnabled, setNarrativeBioReorderingEnabled] = useState(false)
  const [pendingNarrativeBios, setPendingNarrativeBios] = useState<NarrativeBio[]>([])
  const [narrativeBioReorderLoading, setNarrativeBioReorderLoading] = useState(false)

  const handleExpandedChange = (id: number, isExpanded: boolean): void => {
    setExpandedStates(prev => ({ ...prev, [id]: isExpanded }))
  }

  const anyExpanded = (ids: number[]): boolean => ids.some(id => expandedStates[id])

  useEffect(() => {
    setTitle(selectedDataType.description)
    setNumberOfRecords(backgroundInfo != null ? backgroundInfo[selectedDataType.key].length : 0)
  }, [selectedDataType, backgroundInfo])

  const addBackgroundInfoItem = <T extends BaseBackgroundInfo>(
    newItem: T,
    key: keyof PersonBackgroundInfoResponse
  ): void => {
    if (backgroundInfo != null) {
      const currentArray = backgroundInfo[key]
      setBackgroundInfo({
        ...backgroundInfo,
        [key]: [...currentArray, newItem]
      })
    }
  }

  const removeBackgroundInfoItem = <T extends BaseBackgroundInfo>(
    itemToRemove: T,
    key: keyof PersonBackgroundInfoResponse,
    dataTypeCode: string
  ): void => {
    if (backgroundInfo != null) {
      const currentArray = backgroundInfo[key]
      const filteredArray = (currentArray as BaseBackgroundInfo[]).filter(item => item.id !== itemToRemove.id)
      setBackgroundInfo({
        ...backgroundInfo,
        [key]: filteredArray
      })
      setCreatingItems(prev => ({ ...prev, [dataTypeCode]: false }))
    }
  }

  const getNextDisplayOrder = (items: BaseBackgroundInfo[]): number => {
    if (items.length === 0) return 1
    return Math.max(...items.map(item => item.displayOrder)) + 1
  }

  const handleNarrativeBioReorderToggle = (): void => {
    if (narrativeBioReorderingEnabled) {
      void saveNarrativeBioOrder()
    } else {
      setNarrativeBioReorderingEnabled(true)
      setPendingNarrativeBios([...(backgroundInfo?.narrative_bios ?? [])])
    }
  }

  const cancelNarrativeBioReorder = (): void => {
    setNarrativeBioReorderingEnabled(false)
    setPendingNarrativeBios([])
  }

  const saveNarrativeBioOrder = async (): Promise<void> => {
    if (pendingNarrativeBios.length === 0) return

    setNarrativeBioReorderLoading(true)
    try {
      const response = await updateBackgroundInfoDisplayOrder(personId, pendingNarrativeBios, 'narrative_bios')

      if (response.succeeded && response.result !== null) {
        setBackgroundInfo(prev => prev !== null
          ? {
              ...prev,
              narrative_bios: response.result ?? []
            }
          : null)

        showSuccessMessage('Narrative bio order updated successfully')
        setNarrativeBioReorderingEnabled(false)
        setPendingNarrativeBios([])
      } else {
        showErrorMessage('Failed to update narrative bio order')
      }
    } catch (error) {
      showErrorMessage('Failed to update narrative bio order')
      console.error('Error updating narrative bio display order:', error)
    } finally {
      setNarrativeBioReorderLoading(false)
    }
  }

  const handleNarrativeBioDisplayOrderUpdate = async (
    items: NarrativeBio[]
  ): Promise<HttpResponse<NarrativeBio[]>> => {
    setPendingNarrativeBios(items)
    return { succeeded: true, result: items, errors: {}, status: 200, message: 'Success' }
  }

  const handleSaveBackgroundInfoItem = async <T extends BaseBackgroundInfo>(
    item: T,
    dataKey: keyof PersonBackgroundInfoResponse,
    title: string,
    dataTypeCode?: string
  ): Promise<HttpResponse<T>> => {
    const isNewItem = item.id === 0
    const response = isNewItem
      ? await createBackgroundInfo(item, dataKey)
      : await updateBackgroundInfo(item, dataKey)

    if (response.succeeded && response.result != null) {
      setBackgroundInfo(prev => {
        if (prev == null) return prev
        const currentArray = prev[dataKey] as BaseBackgroundInfo[]

        if (isNewItem) {
          const filteredArray = currentArray.filter((existingItem) => existingItem.id !== 0)
          return {
            ...prev,
            [dataKey]: [...filteredArray, response.result]
          }
        } else {
          const updatedArray = currentArray.map((existingItem) =>
            existingItem.id === item.id ? response.result : existingItem
          )
          return {
            ...prev,
            [dataKey]: updatedArray
          }
        }
      })
      if (dataTypeCode != null && dataTypeCode !== '') {
        setCreatingItems(prev => ({ ...prev, [dataTypeCode]: false }))
      }
      showSuccessMessage(`${title} ${isNewItem ? 'created' : 'updated'} successfully`)
    } else {
      showErrorMessage(formatErrorFieldMessages(response.errors))
    }

    return response
  }

  const handleAddItem = (dataType: PersonBackgroundDataType): void => {
    if (backgroundInfo == null) return

    setCreatingItems(prev => ({ ...prev, [dataType.code]: true }))

    const currentItems = backgroundInfo[dataType.key] as BaseBackgroundInfo[]
    const nextDisplayOrder = getNextDisplayOrder(currentItems)

    let newItem: any

    switch (dataType.code) {
      case 'kw-bio':
        newItem = getEmptyKnowWhoBio(personId, nextDisplayOrder)
        break
      case 'narrative-bio':
        newItem = getEmptyNarrativeBio(personId, nextDisplayOrder)
        break
      case 'awards':
        newItem = getEmptyAward(personId, nextDisplayOrder)
        break
      case 'board-directors':
        newItem = getEmptyDirector(personId, nextDisplayOrder)
        break
      case 'education':
        newItem = getEmptyEducation(personId, nextDisplayOrder)
        break
      case 'hobbies-interests':
        newItem = getEmptyHobbyInterest(personId, nextDisplayOrder)
        break
      case 'military-service':
        newItem = getEmptyMilitaryService(personId, nextDisplayOrder)
        break
      case 'notable-facts':
        newItem = getEmptyNotableFact(personId, nextDisplayOrder)
        break
      case 'org-memberships':
        newItem = getEmptyOrgMembership(personId, nextDisplayOrder)
        break
      case 'political-experience':
        newItem = getEmptyPoliticalExperience(personId, nextDisplayOrder)
        break
      case 'previous-occupations':
        newItem = getEmptyPreviousOccupation(personId, nextDisplayOrder)
        break
      case 'publication-credits':
        newItem = getEmptyPublicationCredit(personId, nextDisplayOrder)
        break
      default:
        return
    }

    addBackgroundInfoItem(newItem, dataType.key)
    setCreatingItems(prev => ({ ...prev, [dataType.code]: true }))
  }

  const createMemoStackList = <T extends BaseBackgroundInfo>(
    originalItems: T[],
    getCondensedInfo: (item: T) => React.ReactNode,
    getExpandedInfo: (item: T, disabled: boolean, onDataChange: (newData: T) => void) => React.ReactNode,
    title: string,
    dataKey: keyof PersonBackgroundInfoResponse,
    dataType: PersonBackgroundDataType,
    validateBeforeSave?: (item: T) => string | null
  ): React.ReactNode | null => {
    const isCreating = creatingItems[dataType.code] ?? false

    return React.useMemo(() => (
      backgroundInfo != null && (
        <>
          <DragAndDropStack
            key={`${title.toLowerCase().replace(/\s+/g, '-')}-stack`}
            originalItems={dataType.code === 'narrative-bio' && narrativeBioReorderingEnabled
              ? pendingNarrativeBios as unknown as T[]
              : originalItems}
            itemRenderer={(item: T, index: number) => (
              <BackgroundInfoCard
                index={index}
                originalData={item}
                createCard={item.id === 0}
                modelName={title}
                onExpandedChange={(isExpanded) => { handleExpandedChange(item.id, isExpanded) }}
                getCondensedInfo={(data) => getCondensedInfo(data as T)}
                getExpandedInfo={(data, disabled, onDataChange) => getExpandedInfo(data as T, disabled, onDataChange)}
                onSave={async (item) => {
                  const validationError = validateBeforeSave?.(item as T) ?? null
                  if (validationError != null) {
                    showErrorMessage(validationError)
                    return { succeeded: false, result: null, errors: { occCode: validationError }, status: 400, message: validationError }
                  }
                  return await handleSaveBackgroundInfoItem(item as T, dataKey, title, dataType.code)
                }}
                onDelete={async (item) => {
                  const response = await deleteBackgroundInfo(item, dataKey)

                  if (response.succeeded && response.result != null) {
                    removeBackgroundInfoItem(item, dataKey, dataType.code)
                    showSuccessMessage(`${title} deleted successfully`)
                  } else {
                    const errorMessage = formatErrorFieldMessages(response.errors)
                    showErrorMessage(errorMessage)
                  }
                }}
                onRemove={(itemToRemove) => {
                  removeBackgroundInfoItem(itemToRemove, dataKey, dataType.code)
                }}
              />
            )}
            updateDisplayOrder={dataType.code === 'narrative-bio' && narrativeBioReorderingEnabled
              ? handleNarrativeBioDisplayOrderUpdate as unknown as (items: T[]) => Promise<HttpResponse<T[]>>
              : async () => mockDisplayOrderResponse}
          disableDrag={dataType.code !== 'narrative-bio' || !narrativeBioReorderingEnabled}
          suppressSuccessMessages={dataType.code === 'narrative-bio' && narrativeBioReorderingEnabled}
          makeGray={false}
            id={title.toLowerCase().replace(/\s+/g, '-')}
          />
          {!(dataType.code === 'narrative-bio' && narrativeBioReorderingEnabled) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 2 }}>
              {/* Show reorder button for narrative bios when there are 2+ items */}
              {dataType.code === 'narrative-bio' && originalItems.length > 1 && (
                <IconButtonWithTooltip
                  label="Reorder Narrative Bios"
                  color="primary"
                  onClick={handleNarrativeBioReorderToggle}
                  icon={<ReorderIcon />}
                />
              )}
              <IconButtonWithTooltip
                label={`Add ${title}`}
                color="primary"
                disabled={loading || isCreating}
                onClick={() => { handleAddItem(dataType) }}
                icon={<AddIcon />}
              />
            </Box>
          )}
        </>
      )
    ), [backgroundInfo, (backgroundInfo != null) ? anyExpanded(originalItems.map(item => item.id)) : false, isCreating, narrativeBioReorderingEnabled, pendingNarrativeBios])
  }

  const fetchBackgroundInfo = async (): Promise<void> => {
    try {
      setLoading(true)
      const data = await getPersonBackgroundInfo(personId)
      setBackgroundInfo(data.result)
    } catch (error) {
      showErrorMessage('Failed to fetch background information')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void fetchBackgroundInfo()
  }, [personId])

  const mockDisplayOrderResponse: HttpResponse<any[]> = {
    result: [],
    status: 200,
    succeeded: true,
    message: 'Display order updated successfully',
    errors: null
  }

  const memoizedKnowWhoStack = createMemoStackList<KnowWhoBio>(
    backgroundInfo?.background_infos ?? [],
    (item) => (
      <>
        <Typography component="span" sx={{ fontWeight: 'bold' }}>
          {item.bioType} - &nbsp;
        </Typography>
        <Typography component="span">
          {(item.bioText ?? '').replace(/\n/g, ' ')}
        </Typography>
      </>
    ),
    (item, disabled, onDataChange) => (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <BioTypeSelect
              value={item.bioType}
              updateBioType={(newSource) => {
                onDataChange({ ...item, bioType: newSource ?? '' })
              }}
              disabled={disabled}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Biography Text"
            value={item.bioText}
            onChange={(e) => {
              onDataChange({ ...item, bioText: e.target.value })
            }}
            fullWidth
            multiline
            rows={6}
            disabled={disabled}
          />
        </Grid>
      </Grid>
    ),
    'KnowWho Biography',
    'background_infos',
    DataType.KW_BIO
  )

  const memoizedNarrativeStack = createMemoStackList<NarrativeBio>(
    backgroundInfo?.narrative_bios ?? [],
    (item) => (
      <>
        <Typography component="span" sx={{ fontWeight: 'bold' }}>
          Biography - &nbsp;
        </Typography>
        <Typography component="span">
          {(item.bioText ?? '').replace(/\n/g, ' ')}
        </Typography>
        {hasValue(item.bioSource) && (
          <Typography component="span" sx={{ fontStyle: 'italic', ml: 1 }}>
            (Source: {item.bioSource})
          </Typography>
        )}
      </>
    ),
    (item, disabled, onDataChange) => (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Biography Text"
            value={item.bioText}
            onChange={(e) => {
              onDataChange({ ...item, bioText: e.target.value })
            }}
            fullWidth
            multiline
            rows={6}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={6}>
          <BioSourceSelect
            value={item.bioSource}
            updateBioSource={(newSource) => {
              onDataChange({ ...item, bioSource: newSource ?? '' })
            }}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={6}>
          <MaterialUIPickers
            value={item.bioDate !== null && item.bioDate !== '' ? dayjs(item.bioDate) : null}
            namelabel='Bio Date'
            disabled={disabled}
            onChange={(newDate) => {
              onDataChange({ ...item, bioDate: (newDate != null) ? newDate.format('YYYY-MM-DD') : '' })
            }}
          />
        </Grid>
      </Grid>
    ),
    'Narrative Biography',
    'narrative_bios',
    DataType.NARRATIVE_BIO
  )

  const memoizedAwardStack = createMemoStackList<Award>(
    backgroundInfo?.awards ?? [],
    (item) => (
      <>
        <Link component="button" variant="body1" underline={'hover'} onClick={() => { alert('No Link yet') }}>
          {item.organization} - &nbsp;
        </Link>
        <Typography component="span" sx={{ fontStyle: 'italic' }}>
          {item.awardTitle} &nbsp;
        </Typography>
        {hasValue(item.awardYear) && `(${item.awardYear})`}
      </>
    ),
    (item, disabled, onDataChange) => (
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <AwardTypeSelect
            value={awardTypeOptions.get(item.typeId) ?? { id: 1, code: 'CIV', description: 'Civic Organization' }}
            updateAwardType={(newType) => {
              if (newType != null) {
                onDataChange({ ...item, typeId: newType.id })
              }
            }}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Award Title"
            value={item.awardTitle}
            onChange={(e) => {
              onDataChange({ ...item, awardTitle: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Organization"
            value={item.organization}
            onChange={(e) => {
              onDataChange({ ...item, organization: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Details"
            value={item.details ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, details: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Award Year"
            type="number"
            value={item.awardYear ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, awardYear: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
      </Grid>
    ),
    'Award',
    'awards',
    DataType.AWARDS
  )

  const memoizedDirectorStack = createMemoStackList<Director>(
    backgroundInfo?.directors ?? [],
    (item) => (
      <>
        <Typography component="span" sx={{ fontStyle: 'italic' }}>
          {directorBoardTypeOptions.get(item.typeId)?.description ?? 'Unknown'}
        </Typography>
        {' - '}
        <Typography component="span" color="primary">
          {item.company}
        </Typography>
        {' - '}
        <Typography component="span" fontWeight="bold">
          {item.title}
        </Typography>
      </>
    ),
    (item, disabled, onDataChange) => (
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <DirectorBoardTypeSelect
            value={directorBoardTypeOptions.get(item.typeId) ?? { id: 1, code: 'CIV', description: 'Civic Organization' }}
            updateDirectorBoardType={(newType: DirectorBoardType | null) => {
              if (newType != null) {
                onDataChange({ ...item, typeId: newType.id })
              }
            }}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Title"
            value={item.title}
            onChange={(e) => {
              onDataChange({ ...item, title: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Company"
            value={item.company}
            onChange={(e) => {
              onDataChange({ ...item, company: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Details"
            value={item.details ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, details: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={1.5}>
          <TextField
            label="Start Year"
            type="number"
            value={item.startYear ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, startYear: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={1.5}>
          <TextField
            label="End Year"
            type="number"
            value={item.endYear ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, endYear: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
      </Grid>
    ),
    'Board of Directors',
    'directors',
    DataType.DIRECTORS
  )

  const memoizedEducationStack = createMemoStackList<Education>(
    backgroundInfo?.education ?? [],
    (item) => (
      <>
        <Typography component="span" color="primary">
          {item.schoolName}
        </Typography>
        {item.degreeId != null && item.degreeId !== 0 && (<Typography component="span">
           &nbsp; - {degreeOptions.get(item.degreeId)?.description ?? 'Unknown Degree'}
        </Typography>)}
        {hasValue(item.majors) && (<Typography component="span">
          {` - Majors: ${item.majors}`}
        </Typography>)}
        {' '}
        {formatRange(item.startDate, item.endDate)}
      </>
    ),
    (item, disabled, onDataChange) => (
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <DegreeSelect
            value={degreeOptions.get(item.degreeId) ?? null}
            updateDegree={(newDegree: Degree | null) => {
              if (newDegree != null) {
                onDataChange({ ...item, degreeId: newDegree.id })
              }
            }}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="School Name"
            value={item.schoolName}
            onChange={(e) => {
              onDataChange({ ...item, schoolName: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Majors"
            value={item.majors ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, majors: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Minors"
            value={item.minors ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, minors: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Details"
            value={item.details ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, details: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={1.5}>
          <TextField
            label="Start Year"
            type="number"
            value={item.startDate ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, startDate: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={1.5}>
          <TextField
            label="End Year"
            type="number"
            value={item.endDate ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, endDate: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
      </Grid>
    ),
    'Education',
    'education',
    DataType.EDUCATION
  )

  const memoizedInterestStack = createMemoStackList<HobbyInterest>(
    backgroundInfo?.interests ?? [],
    (item) => (
      <Typography component="span" sx={{ fontStyle: 'italic' }}>
        {item.interest}
      </Typography>
    ),
    (item, disabled, onDataChange) => (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Interest"
            value={item.interest}
            onChange={(e) => {
              onDataChange({ ...item, interest: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
      </Grid>
    ),
    'Hobbies and Interests',
    'interests',
    DataType.HOBBIES_INTERESTS
  )

  const memoizedMilitaryServiceStack = createMemoStackList<MilitaryService>(
    backgroundInfo?.military_service ?? [],
    (item) => (
      <>
        <Typography component="span">
          {item.branch}
        </Typography>
        {formatRange(item.startRank, item.highestRank)}
      </>
    ),
    (item, disabled, onDataChange) => (
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <TextField
            label="Branch"
            value={item.branch}
            onChange={(e) => {
              onDataChange({ ...item, branch: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="Start Rank"
            value={item.startRank ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, startRank: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="Highest Rank"
            value={item.highestRank ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, highestRank: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Medals"
            value={item.medals ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, medals: e.target.value })
            }}
            fullWidth
            disabled={disabled}
            inputProps={{ maxLength: 250 }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Details"
            value={item.details ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, details: e.target.value })
            }}
            fullWidth
            disabled={disabled}
            inputProps={{ maxLength: 250 }}
          />
        </Grid>
      </Grid>
    ),
    'Military Service',
    'military_service',
    DataType.MILITARY_SERVICE
  )

  const memoizedFactsStack = createMemoStackList<NotableFact>(
    backgroundInfo?.facts ?? [],
    (item) => (
      <Typography component="span">
        {item.factText}
      </Typography>
    ),
    (item, disabled, onDataChange) => (
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <TextField
            label="Fact Source"
            value={item.factSource}
            onChange={(e) => {
              onDataChange({ ...item, factSource: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={4}>
          <MaterialUIPickers
            value={item.factDate !== null && item.factDate !== '' ? dayjs(item.factDate) : null}
            namelabel='Fact Date'
            disabled={disabled}
            onChange={(newDate) => {
              onDataChange({ ...item, factDate: (newDate != null) ? newDate.format('YYYY-MM-DD') : '' })
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="Notable Fact"
            value={item.factText}
            onChange={(e) => {
              onDataChange({ ...item, factText: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
      </Grid>
    ),
    'Notable Facts',
    'facts',
    DataType.NOTABLE_FACTS
  )

  const memoizedOrgMembershipStack = createMemoStackList<OrgMembership>(
    backgroundInfo?.memberships ?? [],
    (item) => (
      <>
        <Typography component="span" sx={{ fontStyle: 'italic' }}>
          {membershipTypeOptions.get(item.typeId)?.description ?? 'Unknown'}
        </Typography>
        {' - '}
        <Typography component="span" color="primary">
          {item.organization}
        </Typography>
        {' - '}
        <Typography component="span" fontWeight="bold">
          {item.title}
        </Typography>
      </>
    ),
    (item, disabled, onDataChange) => (
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <MembershipTypeSelect
            value={membershipTypeOptions.get(item.typeId) ?? null}
            updateMembershipType={(newType) => {
              if (newType != null) {
                onDataChange({ ...item, typeId: newType.id })
              }
            }}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Organization"
            value={item.organization}
            onChange={(e) => {
              onDataChange({ ...item, organization: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Title"
            value={item.title}
            onChange={(e) => {
              onDataChange({ ...item, title: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Details"
            value={item.details ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, details: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={1.5}>
          <TextField
            label="Start Year"
            type="number"
            value={item.startYear ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, startYear: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={1.5}>
          <TextField
            label="End Year"
            type="number"
            value={item.endYear ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, endYear: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
      </Grid>
    ),
    'Organization Memberships',
    'memberships',
    DataType.ORG_MEMBERSHIPS
  )

  const memoizedPoliticalExperienceStack = createMemoStackList<PoliticalExperience>(
    backgroundInfo?.experience ?? [],
    (item) => (
      <>
        <Typography component="span" color="primary">
          {item.organization}
        </Typography>
        {' - '}
        <Typography component="span" fontWeight="bold">
          {item.title}
        </Typography>
        {' '}
        {formatRange(item.startDate, item.endDate)}
      </>
    ),
    (item, disabled, onDataChange) => (
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <TextField
            label="Organization"
            value={item.organization ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, organization: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Title"
            value={item.title ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, title: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Details"
            value={item.experienceDesc ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, experienceDesc: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={1.5}>
          <TextField
            label="Start Year"
            type="number"
            value={item.startDate ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, startDate: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={1.5}>
          <TextField
            label="End Year"
            type="number"
            value={item.endDate ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, endDate: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
      </Grid>
    ),
    'Political Experience',
    'experience',
    DataType.POLITICAL_EXPERIENCE
  )

  const memoizedPreviousOccupationStack = createMemoStackList<PreviousOccupation>(
    backgroundInfo?.occupations ?? [],
    (item) => (
      <>
        <Typography component="span" sx={{ fontStyle: 'italic' }}>
          {item.occCode != null ? (occupationTypeOptions.get(item.occCode.id)?.desc ?? 'Unknown') : 'Unknown'}
        </Typography>
        {' - '}
        <Typography component="span" color="primary">
          {item.company}
        </Typography>
        {' - '}
        <Typography component="span" fontWeight="bold">
          {item.occupation}
        </Typography>
        {' '}
        {formatRange(item.startDate, item.endDate)}
      </>
    ),
    (item, disabled, onDataChange) => (
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <OccupationTypeSelect
            value={item.occCode != null ? (occupationTypeOptions.get(item.occCode.id) ?? null) : null}
            updateOccupationType={(newType) => {
              if (newType != null) {
                onDataChange({ ...item, occCode: newType })
              }
            }}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Occupation"
            value={item.occupation}
            onChange={(e) => {
              onDataChange({ ...item, occupation: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Company"
            value={item.company ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, company: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Details"
            value={item.details ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, details: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={1.5}>
          <TextField
            label="Start Year"
            type="number"
            value={item.startDate ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, startDate: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={1.5}>
          <TextField
            label="End Year"
            type="number"
            value={item.endDate ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, endDate: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
      </Grid>
    ),
    'Previous Occupations',
    'occupations',
    DataType.PREVIOUS_OCCUPATIONS,
    (item) => item.occCode == null ? 'Please select an occupation type.' : null
  )

  const memoizedPublicationStack = createMemoStackList<PublicationCredit>(
    backgroundInfo?.publications ?? [],
    (item) => (
      <>
        <Typography component="span" color="secondary">
          {publicationTypeOptions.get(item.typeId)?.description ?? 'Unknown'}
        </Typography>
        {' - '}
        <Typography component="span" fontWeight="bold">
          {item.pubTitle}
        </Typography>
        {' '}
        {hasValue(item.pubYear) && `(${item.pubYear})`}
      </>
    ),
    (item, disabled, onDataChange) => (
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <PublicationTypeSelect
            value={publicationTypeOptions.get(item.typeId) ?? null}
            updatePublicationType={(newType) => {
              if (newType != null) {
                onDataChange({ ...item, typeId: newType.id })
              }
            }}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Publication"
            value={item.pubByIn ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, pubByIn: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Title"
            value={item.pubTitle}
            onChange={(e) => {
              onDataChange({ ...item, pubTitle: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            label="Details"
            value={item.details ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, details: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={1.5}>
          <TextField
            label="Year"
            type="number"
            value={item.pubYear ?? ''}
            onChange={(e) => {
              onDataChange({ ...item, pubYear: e.target.value })
            }}
            fullWidth
            disabled={disabled}
          />
        </Grid>
      </Grid>
    ),
    'Publications',
    'publications',
    DataType.PUBLICATION_CREDITS
  )

  return (
    <Card sx={cardStyles}>
        <EnhancedAccordion expanded={expanded} onExpandedChange={setExpanded}>
        <AccordionSummary expandIcon={<ExpandMoreIcon/>} aria-controls="panel1a-content" id="panel1a-header"
                          sx={accordionSummaryStyles}>

          <div>
            <Typography variant="h6">
              Background Information {!loading && ` - ${title} - Records: ${numberOfRecords}`}
            </Typography>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <CardContent>
            <Box display="flex" gap={0.5}>
              <Box sx={{ minWidth: 'fit-content', flexShrink: 0 }}>
                <Stack>
                  {Object.values(DataType).map((dataType) => (
                    <Box key={dataType.code} display="flex" alignItems="center">
                      <Typography minWidth={25} variant="body2">
                        ({(backgroundInfo != null) ? backgroundInfo[dataType.key].length : 0})
                      </Typography>
                      <IconButtonWithTooltip
                        disabled={loading}
                        label={dataType.description}
                        color={'primary'}
                        placement={'right'}
                        icon={selectedDataType === dataType ? <dataType.selectedIcon /> : <dataType.icon />}
                        onClick={() => { setSelectedDataType(dataType) }}
                      />
                    </Box>
                  ))}
                </Stack>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5, flexShrink: 0 }}>
                <Divider orientation="vertical" flexItem />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {!loading
                  ? (
                  <>
                    <div style={{ display: selectedDataType === DataType.KW_BIO ? 'block' : 'none' }}>
                      {memoizedKnowWhoStack}
                    </div>
                    <div style={{ display: selectedDataType === DataType.NARRATIVE_BIO ? 'block' : 'none' }}>
                      {narrativeBioReorderingEnabled && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h5">Narrative Biography - Reordering Mode</Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButtonWithTooltip
                              icon={<SaveIcon />}
                              label="Save Order"
                              color="primary"
                              onClick={handleNarrativeBioReorderToggle}
                              disabled={narrativeBioReorderLoading}
                            />
                            <IconButtonWithTooltip
                              icon={<CancelIcon />}
                              label="Cancel"
                              color="secondary"
                              onClick={cancelNarrativeBioReorder}
                              disabled={narrativeBioReorderLoading}
                            />
                          </Box>
                        </Box>
                      )}
                      {memoizedNarrativeStack}
                      {narrativeBioReorderLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                          <CircularProgress size={24} />
                        </Box>
                      )}
                    </div>
                    <div style={{ display: selectedDataType === DataType.AWARDS ? 'block' : 'none' }}>
                      {memoizedAwardStack}
                    </div>
                    <div style={{ display: selectedDataType === DataType.DIRECTORS ? 'block' : 'none' }}>
                      {memoizedDirectorStack}
                    </div>
                    <div style={{ display: selectedDataType === DataType.EDUCATION ? 'block' : 'none' }}>
                      {memoizedEducationStack}
                    </div>
                    <div style={{ display: selectedDataType === DataType.HOBBIES_INTERESTS ? 'block' : 'none' }}>
                      {memoizedInterestStack}
                    </div>
                    <div style={{ display: selectedDataType === DataType.MILITARY_SERVICE ? 'block' : 'none' }}>
                      {memoizedMilitaryServiceStack}
                    </div>
                    <div style={{ display: selectedDataType === DataType.NOTABLE_FACTS ? 'block' : 'none' }}>
                      {memoizedFactsStack}
                    </div>
                    <div style={{ display: selectedDataType === DataType.ORG_MEMBERSHIPS ? 'block' : 'none' }}>
                      {memoizedOrgMembershipStack}
                    </div>
                    <div style={{ display: selectedDataType === DataType.POLITICAL_EXPERIENCE ? 'block' : 'none' }}>
                      {memoizedPoliticalExperienceStack}
                    </div>
                    <div style={{ display: selectedDataType === DataType.PREVIOUS_OCCUPATIONS ? 'block' : 'none' }}>
                      {memoizedPreviousOccupationStack}
                    </div>
                    <div style={{ display: selectedDataType === DataType.PUBLICATION_CREDITS ? 'block' : 'none' }}>
                      {memoizedPublicationStack}
                    </div>
                  </>
                    )
                  : (
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '200px',
                    width: '100%'
                  }}>
                    <CircularProgress/>
                  </Box>
                    )}
              </Box>

            </Box>

          </CardContent>
        </AccordionDetails>
        </EnhancedAccordion>
    </Card>
  )
}
